import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import { staffSessionActions } from "@/store/staffSessionSlice"
import { incomeHistoryLabels } from "@/lib/income"
import { benefitReadActions, benefitReadKey } from "./benefitReadSlice"
import { applyEditorField, hasEditorField, validEditorDraft, type EditorDraftData, type EditorFieldChange, type GrantInformationTab } from "./benefitEditorDrafts"
export type { BenefitEditorKind, GrantInformationTab } from "./benefitEditorDrafts"

export type EditorPhase = "opening" | "editing" | "saving" | "conflict" | "outcomeUnknown"
export interface EditorAddress { scopeKey: string; benefitId: string }
interface EditorMetadata extends EditorAddress {
  sessionId: string; token: string | null; readStamp: string | null; section: string; phase: EditorPhase
  fieldErrors: Record<string, string[]>; message: string | null; remoteChangeObserved: boolean
}
export type BenefitEditor = EditorMetadata & EditorDraftData
export type OpenBenefitEditor = Omit<EditorMetadata, "phase" | "fieldErrors" | "message" | "remoteChangeObserved"> & EditorDraftData
export interface StaffCommandRequest { path: string; method: string; body?: string }
export interface BenefitCommand extends EditorAddress {
  id: string; editorSessionId: string | null; request: StaffCommandRequest | null
  phase: "saving" | "reconciling" | "conflict" | "outcomeUnknown" | "abandonedUnknown"
  message: string | null
}
/** Navigation identity only. Grant/review bodies and authority always come from the workspace. */
export interface GrantView extends EditorAddress {
  programId: number; programYear: number; task: "details" | "history" | "payments"; tab: GrantInformationTab
}
export type ProgramTool = "requests" | "bill" | "energy" | "facts"
/** The container needed to return to an editor, not a second copy of the URL tab or saved data. */
// Every Household editor is a card now, so no household tool selection exists.
export type BenefitToolSelection = { section: "program"; tool: ProgramTool }
  | { section: "demographics"; tool: "correction" }
export type BenefitToolView = EditorAddress & BenefitToolSelection
/** One last application-scoped choice, not saved income data or a write token. */
export interface IncomeHistoryPreference extends EditorAddress { incomeType: number }
export interface BenefitEditorState {
  scopeKey: string | null; active: BenefitEditor | null; commands: Record<string, BenefitCommand>; grantView: GrantView | null
  toolView: BenefitToolView | null; incomeHistoryPreference: IncomeHistoryPreference | null
}
const initialState: BenefitEditorState = { scopeKey: null, active: null, commands: {}, grantView: null, toolView: null, incomeHistoryPreference: null }
function incomePreference(active: BenefitEditor | null): IncomeHistoryPreference | null {
  if (active?.kind !== "household-income" || typeof active.fields.incomeType !== "string"
    || !Object.hasOwn(incomeHistoryLabels, active.fields.incomeType)) return null
  return { scopeKey: active.scopeKey, benefitId: active.benefitId, incomeType: Number(active.fields.incomeType) }
}
export const commandKey = (address: EditorAddress) => JSON.stringify([address.scopeKey, address.benefitId])
const same = (first: EditorAddress | null | undefined, next: EditorAddress) => first?.scopeKey === next.scopeKey && first.benefitId === next.benefitId
const blocked = (command: BenefitCommand | undefined) => command && ["saving", "reconciling", "outcomeUnknown", "abandonedUnknown"].includes(command.phase)

export const benefitEditorSlice = createSlice({
  name: "benefitEditors", initialState,
  reducers: {
    incomePreferenceConfirmed(state, { payload }: PayloadAction<{ sessionId: string }>) {
      const active = state.active
      if (active?.sessionId !== payload.sessionId || active.phase !== "editing" || blocked(state.commands[commandKey(active)])) return
      const preference = incomePreference(active)
      if (preference) state.incomeHistoryPreference = preference
    },
    toolViewSelected(state, { payload }: PayloadAction<BenefitToolView>) {
      if (state.scopeKey !== payload.scopeKey || state.active || blocked(state.commands[commandKey(payload)])) return
      // One last tool, bounded across resources. Keeping it through editor completion lets
      // the caller review the saved readback without reopening an invisible container.
      state.toolView = payload
    },
    toolViewClosed(state, { payload }: PayloadAction<EditorAddress & { section: BenefitToolSelection["section"] }>) {
      const command = state.commands[commandKey(payload)]
      if (state.active || command?.phase === "saving" || command?.phase === "outcomeUnknown") return
      // An acknowledged read-only exit must remain possible. Closing this read container
      // neither resolves an unknown command nor releases the command's write fence.
      if (same(state.toolView, payload) && state.toolView?.section === payload.section) state.toolView = null
    },
    grantViewSelected(state, { payload }: PayloadAction<GrantView>) {
      if (state.scopeKey !== payload.scopeKey || state.active || blocked(state.commands[commandKey(payload)])) return
      // Bounded to the last selected task, not an ever-growing per-resource UI cache.
      state.grantView = payload
    },
    grantViewTabChanged(state, { payload }: PayloadAction<EditorAddress & { tab: GrantInformationTab }>) {
      if (same(state.grantView, payload) && state.grantView) state.grantView.tab = payload.tab
    },
    grantViewClosed(state, { payload }: PayloadAction<EditorAddress>) {
      if (same(state.grantView, payload)) state.grantView = null
    },
    openRequested(state, { payload }: PayloadAction<OpenBenefitEditor>) {
      if (state.scopeKey !== payload.scopeKey || state.active || blocked(state.commands[commandKey(payload)]) || !validEditorDraft(payload)) return
      state.active = { ...payload, phase: "opening", fieldErrors: {}, message: null, remoteChangeObserved: false }
    },
    openingCompleted(state, { payload }: PayloadAction<{ sessionId: string }>) {
      if (state.active?.sessionId === payload.sessionId && state.active.phase === "opening") state.active.phase = "editing"
    },
    fieldInitialized(state, { payload }: PayloadAction<EditorFieldChange>) {
      const active = state.active
      if (active?.sessionId !== payload.sessionId || active.kind !== payload.kind || active.key !== payload.key
        || !hasEditorField(active.kind, payload.field) || !["opening", "editing"].includes(active.phase)
        || Object.hasOwn(active.fields, payload.field)) return
      applyEditorField(active, payload, true)
    },
    fieldChanged(state, { payload }: PayloadAction<EditorFieldChange>) {
      const active = state.active
      if (active?.sessionId !== payload.sessionId || active.kind !== payload.kind || active.key !== payload.key
        || !hasEditorField(active.kind, payload.field) || !["opening", "editing"].includes(active.phase)) return
      applyEditorField(active, payload, active.phase === "opening")
    },
    closeRequested(state, { payload }: PayloadAction<{ sessionId: string }>) {
      if (state.active?.sessionId !== payload.sessionId || !["opening", "editing"].includes(state.active.phase)) return
      state.active = null
    },
    conflictReviewRequested(state, { payload }: PayloadAction<{ sessionId: string }>) {
      if (state.active?.sessionId === payload.sessionId && state.active.phase === "editing") state.active.phase = "conflict"
    },
    discardConfirmed(state, { payload }: PayloadAction<{ sessionId: string }>) {
      if (state.active?.sessionId !== payload.sessionId || state.active.phase === "saving") return
      const key = commandKey(state.active)
      const command = state.commands[key]
      if (command?.phase === "outcomeUnknown") command.phase = "abandonedUnknown"
      else if (command?.phase === "conflict") delete state.commands[key]
      state.active = null
    },
    unknownExitAcknowledged(state, { payload }: PayloadAction<EditorAddress>) {
      const command = state.commands[commandKey(payload)]
      if (command?.phase === "outcomeUnknown") command.phase = "abandonedUnknown"
      if (same(state.active, payload) && state.active?.phase === "outcomeUnknown") state.active = null
    },
    unknownResolutionAcknowledged(state, { payload }: PayloadAction<EditorAddress>) {
      const command = state.commands[commandKey(payload)]
      if (command?.phase === "abandonedUnknown") delete state.commands[commandKey(payload)]
    },
    commandRequested(state, { payload }: PayloadAction<BenefitCommand & { supportedRetry?: boolean }>) {
      if (state.scopeKey !== payload.scopeKey) return
      const key = commandKey(payload), previous = state.commands[key]
      if (blocked(previous) && !(payload.supportedRetry && previous?.phase === "outcomeUnknown"
        && previous.id === payload.id && JSON.stringify(previous.request) === JSON.stringify(payload.request))) return
      if (state.active && (!same(state.active, payload) || state.active.sessionId !== payload.editorSessionId
        || !["editing", "outcomeUnknown"].includes(state.active.phase))) return
      state.commands[key] = { id: payload.id, scopeKey: payload.scopeKey, benefitId: payload.benefitId,
        editorSessionId: payload.editorSessionId, request: payload.request, phase: "saving", message: null }
      if (state.active) { state.active.phase = "saving"; state.active.fieldErrors = {}; state.active.message = null }
    },
    commandConfirmed(state, { payload }: PayloadAction<EditorAddress & { id: string }>) {
      const command = state.commands[commandKey(payload)]
      if (command?.id !== payload.id || command.phase !== "saving") return
      // Record the accepted bulk choice with command success, not a mounted component's
      // continuation. Denied/purged/old-session attempts cannot restore this preference.
      if (state.active?.sessionId === command.editorSessionId && command.request?.method === "PUT"
        && command.request.path === `/api/benefitapplications/${encodeURIComponent(payload.benefitId)}/household-income`) {
        const preference = incomePreference(state.active)
        if (preference) state.incomeHistoryPreference = preference
      }
      command.phase = "reconciling"; command.request = null
      if (state.active?.sessionId === command.editorSessionId) state.active = null
    },
    commandRejected(state, { payload }: PayloadAction<EditorAddress & { id: string; outcome: "notSent" | "validation" | "conflict" | "unknown" | "denied"; message: string; fieldErrors: Record<string, string[]> }>) {
      const key = commandKey(payload), command = state.commands[key]
      if (command?.id !== payload.id || command.phase !== "saving") return
      if (payload.outcome === "denied") {
        delete state.commands[key]
        if (same(state.active, payload)) state.active = null
        if (same(state.grantView, payload)) state.grantView = null
        if (same(state.toolView, payload)) state.toolView = null
        if (same(state.incomeHistoryPreference, payload)) state.incomeHistoryPreference = null
        return
      }
      const active = state.active?.sessionId === command.editorSessionId ? state.active : null
      if (payload.outcome === "conflict" || payload.outcome === "unknown") {
        command.phase = payload.outcome === "conflict" ? "conflict" : "outcomeUnknown"
        command.message = payload.message
        if (active) active.phase = command.phase
      } else {
        delete state.commands[key]
        if (active) active.phase = "editing"
      }
      if (active) { active.message = payload.message; active.fieldErrors = payload.fieldErrors }
    },
    snapshotReconciled(state, { payload }: PayloadAction<EditorAddress & { readStamp: string }>) {
      if (state.scopeKey !== payload.scopeKey) return
      if (same(state.active, payload) && state.active) state.active.remoteChangeObserved = state.active.readStamp !== payload.readStamp
      const key = commandKey(payload)
      if (state.commands[key]?.phase === "reconciling") delete state.commands[key]
    },
  },
  extraReducers: builder => {
    builder.addCase(staffSessionActions.scopeRevoked, () => ({ ...initialState, commands: {} }))
    builder.addCase(staffSessionActions.scopeEstablished, (state, { payload }) => {
      if (state.scopeKey !== payload) return { ...initialState, scopeKey: payload, commands: {} }
    })
    builder.addCase(staffSessionActions.contextAccepted, (state, { payload }) => {
      if (state.scopeKey !== payload.scopeKey) return { ...initialState, scopeKey: payload.scopeKey, commands: {} }
    })
    builder.addCase(benefitReadActions.changed, (state, { payload }) => {
      if (payload.phase !== "accessDenied") return
      if (state.active && benefitReadKey({ scopeKey: state.active.scopeKey, id: state.active.benefitId }) === benefitReadKey(payload)) state.active = null
      delete state.commands[commandKey({ scopeKey: payload.scopeKey, benefitId: payload.id })]
      if (same(state.grantView, { scopeKey: payload.scopeKey, benefitId: payload.id })) state.grantView = null
      if (same(state.toolView, { scopeKey: payload.scopeKey, benefitId: payload.id })) state.toolView = null
      if (same(state.incomeHistoryPreference, { scopeKey: payload.scopeKey, benefitId: payload.id })) state.incomeHistoryPreference = null
    })
  },
})
export const benefitEditorActions = benefitEditorSlice.actions
export default benefitEditorSlice.reducer
